import React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import {
  EntitlementDetails,
  LinkedDestinationEntitlement,
  PricingType,
  FetchInvoiceGroupDetailsPpcQuery,
  FetchSandboxParentQuery,
} from '@css-xp/graphtypes/src/generated-types-new';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { isTrialEntitlement } from '@css-xp/entitlements-v2';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { isMultiInstanceAppInstance } from '@css-xp/ppc-utils';
import { SubscriptionDetailsSection } from '../subscription-details-section/subscription-details-section';
import { BillingCycleSection } from '../billing-cycle-section/billing-cycle-section';
import { BillingPaymentDetailsSection } from '../billing-payment-details-section/billing-payment-details-section';
import { TrialBanner } from '../trial-banner/trial-banner';
import { AdditionalStatuspageDetails } from '../additional-details/additional-statuspage-details';
import './entitlement-summary.scss';

export const EntitlementSummary = ({
  entitlement,
  refetch,
  parentProductEntitlements,
  invoiceGroupDetails,
  shouldShowAddOnDetails,
  sandboxParentDetails,
  isDesignIteration1Enabled,
  isManuallyBilledSupportEnabled,
  hideBanner,
  hideDetails,
  isStatusPageEnabled,
  isTWCPhase2Enabled,
  isMultiInstanceAppsEnabled,
  isAppEditionSupportEnabled,
}: {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  parentProductEntitlements: LinkedDestinationEntitlement[];
  invoiceGroupDetails: ApolloQueryResult<FetchInvoiceGroupDetailsPpcQuery | undefined>;
  shouldShowAddOnDetails?: boolean;
  sandboxParentDetails?: {
    parent: FetchSandboxParentQuery | undefined;
    loading: boolean;
  };
  isDesignIteration1Enabled?: boolean;
  isManuallyBilledSupportEnabled?: boolean;
  hideBanner?: boolean;
  hideDetails?: boolean;
  isStatusPageEnabled?: boolean;
  isTWCPhase2Enabled?: boolean;
  isMultiInstanceAppsEnabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
}) => {
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const isStatusPage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isCurrentOfferingPaid = entitlement.offering?.pricingType === PricingType.PAID;
  const isFreePlan = entitlement.edition ? entitlement.edition.includes('Free') : false;
  const isTrial = !isFreePlan && isTrialEntitlement(entitlement, isStatusPage);
  // show trial banner for active trial and upgrade trial flows
  const isTrialBannerEnabled =
    !isStatusPage &&
    (isTrial ||
      (isCurrentOfferingPaid && !!entitlement.upgradeTrial) ||
      (isMultiInstanceAppsEnabled && isMultiInstanceAppInstance(entitlement?.offeringId) && entitlement.upgradeTrial));

  return (
    <>
      {!hideDetails && (
        <div className={isDesignIteration1Enabled ? '' : 'entitlement-summary'}>
          <SubscriptionDetailsSection
            entitlement={entitlement}
            refetch={refetch}
            parentProductEntitlements={parentProductEntitlements}
            shouldShowAddOnDetails={shouldShowAddOnDetails}
            sandboxParentDetails={sandboxParentDetails}
            isManuallyBilledSupportEnabled={isManuallyBilledSupportEnabled}
          />
          <BillingCycleSection entitlement={entitlement} refetch={refetch} />
          {isDesignIteration1Enabled && <div className="mb-24" />}
          <BillingPaymentDetailsSection invoiceGroupDetails={invoiceGroupDetails} />
          {isStatusPageEnabled && isStatusPage && (
            <AdditionalStatuspageDetails
              entitlement={entitlement}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
            />
          )}
        </div>
      )}
      {!hideBanner && isTrialBannerEnabled ? (
        <TrialBanner
          entitlement={entitlement}
          refetch={refetch}
          linkedEntitlements={parentProductEntitlements as []}
          transactionAccountContext={transactionAccountContext}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
          isTWCPhase2Enabled={isTWCPhase2Enabled}
          isAppEditionSupportEnabled={isAppEditionSupportEnabled}
        />
      ) : null}
    </>
  );
};
